// OPERATOR NOTES — what an operator told a task between its runs.
//
// THE GAP THIS CLOSES. `task redispatch` re-ran a failed task with no way to say what
// went wrong: the next run started from the same envelope that produced the failure, so
// an operator who had read the run's tail and knew the fix ("take base's migration meta
// and re-run db:generate") could only watch the same mistake again (babysit task
// tsk_3f80f94844d3aebce5ac4427553c5755, 2026-09-27: ~2 h hand-merging generated Drizzle
// meta). A live run is steered through its mailbox (`message`); a task BETWEEN runs has no
// live anything, so the note has to ride the one thing the next run reads: this envelope.
//
// SERVER-STAMPED: the redispatch control is the only producer and the
// public create door never takes one. The text is scrubbed at that door before it is
// stored, because the envelope is durable and projected on task reads.
//
// BOUNDED ON BOTH AXES, and the bound on the count KEEPS THE NEWEST: a task redispatched a
// sixth time with guidance is one whose latest note is the one that matters, and a list
// that refused the sixth note would refuse exactly the operator who has learned the most.

import { z } from 'zod';

/** Chars one operator note may carry — the `task redispatch` `guidance` cap. */
export const OPERATOR_NOTE_MAX = 2_000;

/** Notes an envelope keeps. Older ones fall off the front ({@link appendOperatorNote}). */
export const OPERATOR_NOTES_MAX = 5;

export const OperatorNoteSchema = z.object({
  /** What the operator said, already scrubbed by the door that stored it. */
  text: z.string().min(1).max(OPERATOR_NOTE_MAX),
  /** When it was given (ISO-8601) — orders the notes against the run history. */
  at: z.string().datetime(),
});
export type OperatorNote = z.infer<typeof OperatorNoteSchema>;

export const OperatorNotesSchema = z.array(OperatorNoteSchema).max(OPERATOR_NOTES_MAX);

/**
 * The notes list with `note` appended, oldest dropped past {@link OPERATOR_NOTES_MAX}.
 * Pure: the caller writes the result back under its own row lock.
 */
export function appendOperatorNote(
  existing: readonly OperatorNote[] | undefined,
  note: OperatorNote,
): OperatorNote[] {
  return [...(existing ?? []), OperatorNoteSchema.parse(note)].slice(-OPERATOR_NOTES_MAX);
}
